/**
 * @file OrderItemRowInput.tsx
 * @description Inline-autocomplete для добавления товара в строку (phase10).
 *
 * Использует тот же endpoint /admin/orders/skus с поиском. Показывает
 * выпадающий список при вводе ≥2 символов. Клик → onSelect SKU.
 */

import { Package, Search } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { formatMoney, listSkusForPicker, type OrderSkuDto } from "../../lib/api-orders";
import { formatSalePackage } from "../../lib/qty-input";

interface Props {
  onSelect: (sku: OrderSkuDto) => void;
}

export function OrderItemRowInput({ onSelect }: Props) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<OrderSkuDto[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await listSkusForPicker({ search: query.trim(), limit: 10 });
        if (!cancelled) {
          setOptions(res);
          setOpen(true);
        }
      } catch {
        /* ignore */
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query]);

  function pick(sku: OrderSkuDto) {
    onSelect(sku);
    setQuery("");
    setOptions([]);
    setOpen(false);
  }

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-on-surface-variant" />
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => options.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Начните вводить название или SKU…"
        className="pl-9"
      />
      {open && options.length > 0 ? (
        <ul className="absolute z-10 left-0 right-0 top-full mt-1 max-h-64 overflow-y-auto rounded-md border border-on-surface/8 bg-surface-lowest shadow-lg">
          {options.map((s) => (
            <li
              key={s.id}
              className="flex items-center gap-2 px-3 py-2 hover:bg-surface/40 cursor-pointer"
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s);
              }}
            >
              <Package className="size-4 text-on-surface-variant shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium truncate">
                    {s.productName}
                  </span>
                  <Badge variant="outline" className="text-[10px] font-mono">
                    {s.code}
                  </Badge>
                </div>
                <div className="text-[10px] text-on-surface-variant truncate">
                  {/* Фасовка через реестр единиц: «50 г», не «50.000 g» (TD-53). */}
                  {s.saleQuantity && s.saleUnit
                    ? `${formatSalePackage(s.saleQuantity, s.saleUnit)} · `
                    : ""}
                  НДС {s.vatRate}%
                </div>
              </div>
              <span className="text-xs font-semibold text-primary shrink-0">
                {formatMoney(s.priceGross)} ₽
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
